import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  HStack,
  Text,
} from '@chakra-ui/react';
import { X } from 'lucide-react';
import { IManualEntryFieldSelected, ManualEntryScopeType } from '../../../../models/m6-2';
import { ManualEntryFieldSelector } from './ManualEntryFieldSelector';
import { ManualEntryForm } from './ManualEntryForm';
import { leoTokens } from '../../theme/leoTokens';

type WizardStep = 1 | 2;

interface ManualEntryWizardState {
  step: WizardStep;
  fields: IManualEntryFieldSelected[];
}

interface ManualEntryWizardProps {
  open: boolean;
  onClose: () => void;
  /** ID del team corrente (per preferenze scope) */
  teamId: string;
  /** Nome del team (per label banner preferenze) */
  teamName?: string;
  /** Callback post-submit */
  onCompleted?: () => void;
}

/**
 * Wizard a 2 step per l'input manuale avanzato (M6.2 B13).
 *
 * Step 1 — ManualEntryFieldSelector: checklist categorizzata di campi con
 *   memoria preferenze per team. Richiede `scopeType='team'` + `scopeId=teamId`.
 *
 * Step 2 — ManualEntryForm: form dinamico basato sui campi scelti nello step 1.
 *   Slide back torna allo step 1.
 *
 * Il componente è standalone e non dipende da ImportCSVWizard — si affianca
 * a ManualSessionDialog come "modalità avanzata" (più campi, personalizzabili).
 */
export const ManualEntryWizard: React.FC<ManualEntryWizardProps> = ({
  open,
  onClose,
  teamId,
  teamName,
  onCompleted,
}) => {
  const [state, setState] = useState<ManualEntryWizardState>({
    step: 1,
    fields: [],
  });

  const handleFieldsConfirmed = useCallback((fields: IManualEntryFieldSelected[]) => {
    setState({ step: 2, fields });
  }, []);

  const handleBack = useCallback(() => {
    setState((prev) => ({ ...prev, step: 1 }));
  }, []);

  const handleSubmit = useCallback(async (data: Record<string, unknown>) => {
    // Placeholder integrazione endpoint POST /sessions/manual — Sprint 6.
    // Per ora chiude il wizard e notifica il parent.
    onCompleted?.();
    onClose();
    // Reset per prossima apertura
    setState({ step: 1, fields: [] });
  }, [onClose, onCompleted]);

  const handleClose = useCallback(() => {
    onClose();
    setState({ step: 1, fields: [] });
  }, [onClose]);

  const scopeType: ManualEntryScopeType = 'team';

  if (!open) return null;

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex={1300}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="blackAlpha.500"
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <Box
        bg="var(--c-card-bg)"
        borderRadius="xl"
        boxShadow="2xl"
        width="100%"
        maxW="520px"
        maxH="90vh"
        overflowY="auto"
        mx={4}
      >
        {/* Header */}
        <HStack
          px={5}
          py={4}
          borderBottom="1px solid"
          borderColor={leoTokens.divider}
          justify="space-between"
          align="center"
        >
          <Box>
            <Text fontWeight={700} fontSize="15px" color={leoTokens.ink}>
              Inserimento manuale avanzato
            </Text>
            <Text fontSize="12px" color={leoTokens.muted} mt="1px">
              Step {state.step} di 2 &mdash;{' '}
              {state.step === 1 ? 'Seleziona i campi' : 'Compila la sessione'}
            </Text>
          </Box>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleClose}
            px={2}
            aria-label="Chiudi"
          >
            <X size={16} />
          </Button>
        </HStack>

        {/* Step indicator */}
        <HStack px={5} pt={3} pb={1} gap={2}>
          {([1, 2] as const).map((s) => (
            <Box
              key={s}
              flex={1}
              h="3px"
              borderRadius="full"
              bg={state.step >= s ? leoTokens.brand : leoTokens.divider}
              transition="background 0.2s"
            />
          ))}
        </HStack>

        {/* Body */}
        <Box px={5} py={4}>
          {state.step === 1 ? (
            <ManualEntryFieldSelector
              scopeType={scopeType}
              scopeId={teamId}
              scopeLabel={teamName}
              onConfirm={handleFieldsConfirmed}
            />
          ) : (
            <ManualEntryForm
              fields={state.fields}
              onSubmit={handleSubmit}
              onBack={handleBack}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
